import React, { useEffect, useState } from 'react';
import ShopMap from './ShopMap';
import {
  hasPoint, isLoudestTagAnswer, loadShopMap, locationCaption, locationZones, palletZones, zonePct, zoneStrength,
} from '../utils/shopMap';

// Where a beacon is, under a Find (FindProgress) or wherever a LocationView is shown: one line
// (locationCaption: "Press brake area or Main metal rack · now") and the shop map with its pallet
// zones, the most likely one shaded strong and, while it is under 70 % sure, the second one light
// (Mike, 2026-10-21: no heat map and no dot: a dot looked exact when a reading is ~14 ft off at the
// median). The whole shop by default; "Zoom" frames the shaded zones.
//
// Reads: the map once per page (utils/shopMap loadShopMap, read again when the location names
// another map rev), only when there is something to draw (a zone answer, a dock, or a point on a map
// without zones). A docked beacon draws its dock as a dot, no zone. A failed read leaves the caption.

const styles = {
  box: { maxWidth: 640, width: '100%' },
  caption: { fontWeight: 600, color: 'var(--bs-body-color, #212529)' },
  bar: { display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0 4px', flexWrap: 'wrap' },
  toggle: { padding: '1px 10px', fontSize: '0.78rem' },
  legend: { fontSize: '0.72rem', color: 'var(--bs-secondary-color, #6c757d)' },
};

const CAPTION_TICK_MS = 30 * 1000;

/**
 * "Most likely Press brake area (62 %), else Main metal rack (30 %)" under the location model; under the
 * loudest-tag rule "Press brake area: tag 10 loudest (-55 dBm), 6 dB clear of the next zone" or, within 3 dB,
 * "Press brake area (tag 10, -59 dBm) or Main metal rack (tag 9, -60 dBm): their tags are within 3 dB".
 * '' without a zone answer.
 */
export function zoneLegend(location) {
  const z = location && location.zone;
  if (!z || !z.name) return '';
  const rough = location.rough || location.best === 'gateways' ? ' (rough: gateways only)' : '';
  const z2 = location.zone2;
  if (isLoudestTagAnswer(location)) {
    const who = (q) => [q.tag !== null && q.tag !== undefined ? `tag ${q.tag}` : null, zoneStrength(q) || null]
      .filter(Boolean).join(', ');
    if (z2 && z2.name) return `${z.name} (${who(z)}) or ${z2.name} (${who(z2)}): their tags are within 3 dB${rough}`;
    const m = Number(location.zone_margin_db);
    const head = z.tag !== null && z.tag !== undefined ? `tag ${z.tag} loudest` : 'loudest tag';
    const strength = zoneStrength(z) ? ` (${zoneStrength(z)})` : '';
    const tail = location.zone_margin_db !== null && location.zone_margin_db !== undefined && Number.isFinite(m)
      ? `, ${Math.round(m * 10) / 10} dB clear of the next zone` : ', the only zone tag heard';
    return `${z.name}: ${head}${strength}${tail}${rough}`;
  }
  if (z2 && z2.name) return `Most likely ${z.name} (${zonePct(z)}), else ${z2.name} (${zonePct(z2)})${rough}`;
  return `${z.name} (${zonePct(z)})${rough}`;
}

export default function BeaconLocation({ beaconId, location, beacon = null, className = '', style = undefined }) {
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const hasZone = !!(location && location.zone && location.zone.name);
  const drawable = hasZone || hasPoint(location);
  const mapRev = location && location.map_rev !== undefined ? location.map_rev : null;

  useEffect(() => {
    if (!drawable) return undefined;
    let live = true;
    loadShopMap(mapRev).then((r) => { if (live && r.ok && r.body && r.body.map) setMap(r.body); });
    return () => { live = false; };
  }, [drawable, mapRev]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), CAPTION_TICK_MS);
    return () => clearInterval(t);
  }, []);

  if (!location) return null;
  const caption = locationCaption(location, now, beacon);
  const zones = map ? map.zones : null;
  const matched = map ? locationZones(location, zones) : { best: null };
  // zoom frames the shaded zones; without a zone answer (a dock, an estimate from before the zones) the dot
  const canZoom = !!matched.best || hasPoint(location);
  const legend = matched.best ? zoneLegend(location) : '';

  return (
    <div className={className} style={{ ...styles.box, ...(style || {}) }} data-testid="beacon-location">
      {caption && <div className="small" style={styles.caption} data-testid="beacon-location-caption">{caption}</div>}
      {map && drawable && (
        <>
          <div style={styles.bar}>
            {canZoom && (
              <div className="btn-group btn-group-sm" role="group" aria-label="Map view">
                <button type="button" className={`btn ${!zoom ? 'btn-secondary' : 'btn-outline-secondary'}`} style={styles.toggle}
                  aria-pressed={!zoom} onClick={() => setZoom(false)}>Whole shop</button>
                <button type="button" className={`btn ${zoom ? 'btn-secondary' : 'btn-outline-secondary'}`} style={styles.toggle}
                  aria-pressed={zoom} onClick={() => setZoom(true)}>Zoom</button>
              </div>
            )}
            {legend && <span style={styles.legend} data-testid="beacon-location-legend">{legend}</span>}
          </div>
          {/* where a pallet is: the zones, walls, racks and machines; the radio gear (anchors,
              gateways) and the old rack spots belong to the /shop-map editor */}
          <ShopMap map={map} location={location} focus={zoom && canZoom ? location : null}
            showAnchors={false} showGateways={false} showSpots={!palletZones(zones).length}
            title={`Where ${beaconId || 'the beacon'} is${caption ? `: ${caption}` : ''}`} />
        </>
      )}
    </div>
  );
}
